import React, { useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import RatingStars from './RatingStars';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';

const ReviewForm = ({ productId, orderId, productName, onSubmit, onCancel }) => {
  const { user, token } = useAuth();
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const MAX_IMAGES = 5;
  const MIN_TEXT_LENGTH = 10;
  const MAX_TEXT_LENGTH = 1000;

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > MAX_IMAGES) {
      toast.warning(`En fazla ${MAX_IMAGES} fotoğraf yükleyebilirsiniz`);
      return;
    }

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
        toast.error(`${file.name} desteklenmeyen format`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error(`${file.name} çok büyük (max 5MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    setImages(prev => [...prev, ...validFiles]);
  };

  // Remove image
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (rating < 1 || rating > 5) {
      toast.error('Lütfen bir puan seçin');
      return;
    }

    if (reviewText.trim().length < MIN_TEXT_LENGTH) {
      toast.error(`Yorum en az ${MIN_TEXT_LENGTH} karakter olmalıdır`);
      return;
    }

    if (reviewText.trim().length > MAX_TEXT_LENGTH) {
      toast.error(`Yorum en fazla ${MAX_TEXT_LENGTH} karakter olabilir`);
      return;
    }

    setSubmitting(true);

    try {
      // Prepare FormData
      const formData = new FormData();
      formData.append('rating', rating);
      formData.append('review_body', reviewText.trim());
      formData.append('author_name', user?.full_name || 'Anonim');
      if (orderId) {
        formData.append('order_id', orderId); // OrderId'yi backend'e gönder
      }

      // Append images
      images.forEach((image, index) => {
        formData.append('images', image);
      });

      // Submit
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/products/${productId}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('🎉 Yorumunuz başarıyla eklendi! Admin onayından sonra yayınlanacaktır.');
        if (onSubmit) onSubmit();
        // Reset form
        setRating(5);
        setReviewText('');
        setImages([]);
        setImagePreviews([]);
      } else {
        // Backend'den gelen hata mesajını göster
        const errorMessage = data.error || data.message || 'Yorum eklenemedi';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Yorum gönderme hatası:', error);
      toast.error('Yorum eklenirken hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Değerlendirme Yazın
        </h3>
        <p className="text-sm text-gray-600">
          {productName} ürünü hakkındaki deneyiminizi paylaşın
        </p>
      </div>

      {/* Rating */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Puanınız *
        </label>
        <div className="bg-white p-4 rounded-xl border-2 border-gray-200">
          <RatingStars 
            rating={rating}
            onChange={setRating}
            size="xl"
            readonly={false}
            className="justify-center"
          />
          <p className="text-center text-sm text-gray-600 mt-2">
            {rating === 5 && '⭐ Mükemmel'}
            {rating === 4 && '😊 Çok İyi'}
            {rating === 3 && '👍 İyi'}
            {rating === 2 && '😐 Orta'}
            {rating === 1 && '😞 Kötü'}
          </p>
        </div>
      </div>

      {/* Review Text */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Yorumunuz *
        </label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Ürün hakkındaki düşüncelerinizi paylaşın... Kalite, beden, kumaş gibi detaylar ekleyebilirsiniz."
          rows={6}
          maxLength={MAX_TEXT_LENGTH}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-green focus:ring-2 focus:ring-green-100 transition-all resize-none"
          required
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-gray-500">
            En az {MIN_TEXT_LENGTH} karakter gerekli
          </p>
          <p className={`text-sm font-medium ${
            reviewText.length > MAX_TEXT_LENGTH ? 'text-red-600' : 'text-gray-600'
          }`}>
            {reviewText.length} / {MAX_TEXT_LENGTH}
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Fotoğraflar (İsteğe Bağlı)
        </label>
        <p className="text-xs text-gray-500 mb-3">
          En fazla {MAX_IMAGES} fotoğraf, her biri max 5MB
        </p>

        {/* Image Previews */}
        {imagePreviews.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-3">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-24 h-24 object-cover rounded-lg shadow-md"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload Button */}
        {images.length < MAX_IMAGES && (
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageSelect}
              className="hidden"
            />
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brand-green hover:bg-green-50 transition-all">
              <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">
                Fotoğraf Yükle
              </p>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG veya WebP formatında
              </p>
            </div>
          </label>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={submitting || reviewText.trim().length < MIN_TEXT_LENGTH}
          className="flex-1 bg-brand-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Gönderiliyor...</span>
            </span>
          ) : (
            '✍️ Yorumu Gönder'
          )}
        </button>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            İptal
          </button>
        )}
      </div>
    </form>
  );
};

export default ReviewForm;
